export interface Contact {
  id: number;
  type: string;
  value: string;
}

export interface Address {
  id: number;
  lines: string[];
  city: string;
  state: string;
  postalCode: string;
  country: string;
  type: string;
}

export interface ProfileProps {
  fullName: string;
  id: string;
  addresses: Address[];
  contacts: Contact[];
}

export interface Identifier {
  type: string;
  value: string;
}

export interface Draw {
  amount: number;
  apr: number;
  numberOfPayments: number;
  outstandingPrincipal: number;
  periodicPayment: number;
  totalInterest: number;
}

export interface LoC {
  creditLimit: number;
  balanceOutstanding: number;
  availableCredit: number;
  id: string;
  status: string;
  drawOnHold: boolean;
  periodicPayment?: number;
  annualPercentageRate?: number;
  outstandingPrincipal?: number;
  paymentFrequency?: string;
  term?: string;
  type?: 'LINE_OF_CREDIT';
  reamortizationStatus?: string | null;
  instantFunding?: {
    eligible: boolean;
    cardInfo: object;
  };
}

export interface TermLoan {
  approvedAmount: number;
  balanceOutstanding: number;
  balancePaid: number;
  estimatedPaymentsRemaining: number;
  estimatedPayoffAmount?: number;
  estimatedPayoffDate?: string;
  id: string;
  type: 'FIXED_TERM';
  percentagePaidDown: number;
  term: string;
  status: string;
}

export interface LoanActivity {
  amount: number;
  beginningBalance: number;
  clearedDate: string;
  currency: string;
  initiatedDate: string;
  interest: number;
  loanId: string;
  principal: number;
  remainingBalance: number;
  status: string;
  transactionSetId: string;
  type: string;
  loanType: string;
}

export interface Session {
  status: string;
  client_message: string;
  data?: {
    session_identifier: string;
    authorized_url: string;
    authorized_url_expiration_utc: string;
    participant_unique_id1: string;
    participant_unique_id2?: string;
  },
  time: string;
}

export interface Documents {
  loanId: string;
  loanType: string;
  documentId: number;
  documentType: 'MONTHLY_STATEMENT' | 'SIGNED_CONTRACT';
  fileName: string;
  createdOn: string;
}

export interface User {
  email: string;
  userId: string;
}

export type AppStatus =
  |'APPROVED'
  |'CLOSED_LOST'
  |'CLOSED_WON'
  |'NOT_APPROVED'
  |'PREPROCESSED'
  |'REROUTED_LEGACY'
  |'STARTED'
  |'SUBMITTED'
  |'SYSTEM_GENERATED'
  |'APPLICATION_DORMANT'
  |'FUNDING';

  type createdBy = 'SoFi' | string;
  type regionCode = 'USA' | string;

type FundsAvailability =
  | 'ONE_DAY'
  | 'TWO_TO_THREE_DAYS'
  | 'ONE_WEEK'
  | 'TWO_WEEKS'
  | 'THREE_TO_FOUR_WEEKS'
  | 'OVER_ONE_MONTH';

export type Person = {
  id: string;
  type: string;
  typeData: {
    ownershipPct: number;
    hasBeneficialOwner: boolean;
    hasManagingPerson: boolean;
  };
  href: string;
  firstName: string;
  middleName: string;
  lastName: string;
  prefix: string;
  suffix: string;
  preferredName: string;
  dateOfBirth: string;
  identifiers: Identifier[];
  addresses: [
    {
      type: "PHYSICAL_ADDRESS",
      lineOne: string,
      lineTwo?: string,
      city: string,
      state: string,
      postalCode: string
    }
  ];
  contacts: Contact[];
  createdAt: string;
  createdBy: string;
};

export type Business = {
  id: string;
  type: string;
  href: string;
  legalName: string;
  doingBusinessAs: string;
  legalEntityType: string;
  identifiers: Identifier[];
  addresses: [
    {
      type: "PHYSICAL_ADDRESS",
      lineOne: "string",
      lineTwo: "string",
      city: "string",
      state: "string",
      postalCode: "string"
    }
  ];
  contacts: Contact[];
  inceptionDate: string;
  trustInfo: {
    name: string;
    type: string;
  };
  createdAt: string;
  createdBy: string;
  stateOfIncorporation: string;
  naicsCode: string;
};

export interface Party {
  id: string;
  persons: Person[];
  businesses: Business[];
  createdAt: string;
  createdBy: string;
}

export interface AppData {
  /** Applicant email as stored at create (data.email); the party contact is the fallback. */
  email?: string;
  /** Legal business name written by prove/complete (PROVE_STEP); party.businesses[0].legalName is the fallback. */
  businessName?: string;
  /** ISO timestamp of the applicant's Prove prefill consent; its presence is the run-once gate for PROVE_STEP. */
  provePrefillConsent?: string;
  desiredAmountToBorrow: number;
  desiredFundsAvailability: FundsAvailability;
  desiredUseOfFunds: string;
  party: Party;
  averageBankBalance: number;
  estimatedAnnualRevenue: number;
  creditConsent: boolean;
  eSignConsent: boolean;
  // ISO date-time; set when the merchant picks a delayed funding date at checkout.
  delayedFundingOn?: string;
  tlpConsent: string;
  thirdPartyLenders: [
    {
      id: string;
      competitorName: string;
      currentBalance: number;
      payeeName: string;
      payoffAmount: number;
      payoffToExistingLender: boolean;
      existingLenderName: string;
      payoffWireSent: boolean;
      payoffWireDate: string;
    }
  ],
}

export interface Application {
  id: string;
  version: number;
  type: 'NEW' | 'RENEWAL';
  href: string;
  userId: string;
  status: AppStatus;
  data: AppData;
  sourcePartyId: string;
  metadata: {
    updatedAt: string;
    sfOpportunityId: string;
    sfLeadId: string | null;
    channelCode: string;
    createdBy: createdBy;
    regionCode: regionCode;
    productCode: string;
    experienceType: 'configurable' | string;
    referralSource?: string;
  };
  properties: {
    data: {
      expressEstimateValue?: number;
      showEstimate?: boolean;
    };
  };
};

export interface Offer {
  offerId: string;
  term: number;
  maxLineAmount: number;
  minLineAmount: number;
  defaultPaymentFrequency: string;
  eligiblePaymentFrequencies: string[];
  originationFeePct: number;
  prePaymentDiscountPct: number | null;
  centsOnDollar: number;
  product: 'TermLoan' | 'LineOfCredit';
  pricedDetails: PricedDetails;
  presentableDisbursementAmt: number;
  requiredInitialDraw: number;
  returnedPaymentFee: number;
  lateFee: number;
  prePaymentDiscountRelatedOfferIds: Record<string, string>;
  userActionStatus: 'HIDDEN' | 'NOT_SELECTED' | 'REJECTED' | 'SELECTED';
  presentationStatus: 'ACTIVE' | 'INACTIVE' | 'EXPIRED';
  [x: string]: unknown;
}

export interface SelectResponse {
  applicationId: string;
  decisionId: string;
  effectiveDate: string;
  experienceType: string;
  expirationDate: string;
  offers: Offer[];
  selectedOffer: string;
}

export interface PricedDetails {
  lineAmount: number;
  trueCentsOnDollar: number;
  trueAPR: number;
  paymentFrequency: string;
  payment: number;
  disbursementAmount: number;
  numberOfPayments: number;
  lender: string;
  totalAmountPaidBack: number;
  loanOriginationFee: number;
  term: number;
  totalLoanCost: number;
  averageMonthlyPayment: number;
  modelId: string;
  monthlyFee: number;
  [x: string]: unknown;
}

export interface NextPayment {
  loanId: string;
  loanStatus: 'NORMAL' | 'ARRANGEMENT' | 'PAST_DUE';
  onHold: boolean;
  frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  currency: 'USD';
  nextPaymentAmount: number;
  nextPaymentDate: string;
  payment?: {
    amount: number;
    scheduledDate: string;
  };
  estimatedPaymentsRemaining: number;
}

export interface Profile {
  firstName: string;
  lastName: string;
  fullName: string;
  preferredName: string;
  href: string;
  id: string;
  addresses: Address[];
  contacts: Contact[];
}

export interface BusinessProfile {
  id: string;
  legalName: string;
  doingBusinessAs: string;
  legalEntityType:
    | 'COMPANY'
    | 'SOLE_TRADER'
    | 'PARTNERSHIP'
    | 'TRUST'
    | 'SOLE_PROPRIETOR'
    | 'LLC'
    | 'CORPORATION'
    | 'GENERAL_PARTNERSHIP'
    | 'LP'
    | 'LLP'
    | 'NON_PROFIT'
    | 'INDEPENDENT_CONTRACTOR'
    | 'S_CORP'
    | 'C_CORP'
    | 'TRIBAL_BUSINESS'
    | 'ELIGIBLE_SELF_EMPLOYED_INDIVIDUAL'
    | 'VETERANS_ORG';
  href: string;
  addresses: Address[];
  contacts: Contact[];
  identifiers: Identifier[];
}

export interface LDR {
  id: number;
  opportunityId: string;
  salesforceId: string;
  createdAt: string;
  createdBy: string;
  classification: string;
  issueType: string;
  merchantRequest: string;
  status: string;
  requestType: string;
  details: string;
  explanation: string;
  rejectionReason: string | null;
  reviewedDateTime: string | null;
  userSubmittedDateTime: string | null;
  merchantProvidedExplanation: string | null;
  documentSalesforceId: string | null;
  attachments: any[];
  source: string;
}

export interface RenewalEligibility {
  loceligible: boolean;
  termLoanEligible: boolean;
}

export interface ODORenewal {
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onDismiss?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  cta?: string;
  title?: string;
  message: string;
}

export interface Parties {
  parties: [
    {
      businesses: [
        {
          href: string;
          id: string;
          legalName: string;
        }
      ];
      id: string;
      loans: [
        {
          href: string;
          id: string;
          loanType: string;
        }
      ];
      people: [
        {
          href: string;
          id: string;
          name: string;
        }
      ];
      type: string;
    }
  ];
  user: {
    href: string;
    id: string;
    name: string;
  };
}

export interface OTPData {
  bankAccount: string;
  bankName: string;
  lowerDraw: number;
  nextBusinessDay: string;
  nextPaymentAmount: number;
  nextPaymentDate: string;
  pastDue: number;
  remainingBalance: number;
  upperDraw: number;
}

export interface rawOffersProps {
  customerData: CustomerData,
  loanDetails?: LoanDetails,
  applicationOffer: {
    expirationDate?: string,
    experienceType: string;
    selectedOffer: string | null;
    offers: Offer[];
    [x: string]: unknown;
  };
  // Only populated for SCC-brute-force (repeat PARTNER_ISO) applications
  partnerAccountName?: string;
  partnerAccountDba?: string;
  [x: string]: unknown;
}

export interface offerPreviewData {
  offerId: string;
  prevInterest: number | undefined;
  remainingPrincipal: number | undefined;
  lineAmount: number;
  disbursementAmount: number;
  totalAmountPaidBack: number;
  loanOriginationFee: number;
  totalLoanCost: number;
  term: number;
  paymentFrequency: string;
  trueAPR: number;
  trueCentsOnDollar: number;
  payment: number;
  averageMonthlyPayment: number;
  monthlyFee: number;
  requiredInitialDraw: number;
  returnedPaymentFee: number;
  lateFee: number;
  prePaymentDiscountPct: number | null;
}

export interface termLoanOfferProps {
  offerPreviewData: offerPreviewData;
  applicationType: string | null;
  businessState: string;
}

export interface SalesAgentInfo {
  name: string,
  email: string,
  phone: string,
  fax: string
}

export interface LoanDetails {
  remainingPrincipal: number;
  remainingInterest: number;
  loanAmount: number;
  disbursementAmount: number;
  term: number;
  paymentFrequency: string;
  [x: string]: unknown;
}

export interface CustomerData {
  businessCountry: string;
  businessState: string;
  legalEntity: string;
  [x: string]: unknown;
}

export interface RawCheckoutData {
  data: {
    checkoutUuid: string;
    applicationUuid: string;
    offerUuid: string;
    selectedDrawAmount: string;
    estimatedPaymentAmount: number;
    checkoutStatus: CheckoutStatus;
    disclosureUuid?: string;
    routingNumber?: string;
    identityVerificationStatus?: IdentityStatus;
    plaidOverride: boolean;
  };
};

export interface CheckoutData {
  checkoutUuid: string;
  applicationUuid: string;
  offerUuid: string;
  selectedDrawAmount: string;
  estimatedPaymentAmount: number;
  checkoutStatus: CheckoutStatus;
  disclosureUuid?: string;
  routingNumber?: string;
  identityVerificationStatus?: IdentityStatus;
  plaidOverride: boolean;
  isFincenAdditionalOwnersPassed?: boolean;
  isFincenControlPersonPassed?: boolean;
};

export enum CheckoutStatus {
  NOT_FOUND = "NOT_FOUND",
  INITIALIZED = "INITIALIZED",
  UPDATED = "UPDATED",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  VOID = "VOID"
}

export enum IdentityStatus {
  NOT_STARTED = "NOT_STARTED",
  PASS = "PASS",
  FAIL = "FAIL",
  CHALLENGE = "CHALLENGE"
}

export interface MarketingPageData {
  ref?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  bestContactNumber?: string;
  grossAnnualRevenue?: number;
  channelCode?: string;
}

export interface MerchantBankInfo {
  bankName: string;
  accountNumber: string;
}

export interface MerchantProfile {
  merchantBankInfo: MerchantBankInfo;
}

export interface PlaidMerchantInfo {
  account_last_four: string;
  plaid_item_id: string;
  account_type: string;
  merchant_id: string;
  association_type: string;
  association_id: string;
  institution_name: string;
  ExpirationTime: number;
  creation_time: number;
}

export interface PlaidMerchantInfoResponse {
  plaidMerchantInfo: PlaidMerchantInfo | null;
  assetsRefreshOcrolusStatus: string | null;
}

export interface BankData {
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface ValidatedAccountNumber {
  isValid: boolean;
}

export interface Identity {
  idNumber: string;
  questions: IdentityQuestion[];
}

export interface IdentityAnswers {
  idNumber: string;
  answers: IdentityAnswer[];
}

export interface IdentityAnswer {
  type: string;
  answer: string;
}

export interface IdentityQuestion {
  prompt: string;
  type: string;
  answerList: string[];
}

export interface CheckoutBlockingErrorContext {
  salesAgentEmail: string;
  opportunityId: string;
  salesAgentAction: string;
  errorLevel: string;
}
